import type { CompletionSource } from '@codemirror/autocomplete';
import {
  CompletionContext,
  autocompletion,
  startCompletion,
} from '@codemirror/autocomplete';
import { syntaxTree } from '@codemirror/language';
import type { Extension } from '@codemirror/state';
import { EditorView } from '@codemirror/view';

import type { OverlayTriggerState } from '../../../overlays/hooks/useOverlay.js';
import { ANNOTATION_SUGGESTION_APPLY } from '../editor-config.js';
import type { FilterFieldSuggestion } from './Suggestion.js';
import type { useCachedTreeConversion } from '../hooks/useCachedTreeConversion.js';
import { defaultLogicalOperatorSuggestions } from '../parser/filterfield-language.js';
import type { NumberFormatParser } from '../parser/getNumberParser.js';
import type { FilterFieldSuggestionsCallback } from '../types/suggestions.js';
import type {
  FilterFieldLeafNode,
  FilterFieldLezerNodeType,
  FilterFieldStatementNode,
  FilterFieldValueNode,
} from '../types/tree-nodes.js';
import type { FilterFieldValidatorMap } from '../types/validation.js';
import { getTextFromRange } from '../utils/get-text.js';
import { getKeySuggestions } from '../utils/suggestions-utils/key-suggestions.js';
import { getComparisonOperatorSuggestions } from '../utils/suggestions-utils/operator-suggestions.js';
import { getNoMatchingSuggestion } from '../utils/suggestions-utils/shared-suggestions.js';
import { getFilterStatementForSuggestions } from '../utils/suggestions-utils/suggestions-callback.js';
import { getValueSuggestions } from '../utils/suggestions-utils/value-suggestions.js';
import {
  isFilterFieldLeafNode,
  isFilterFieldListNode,
  isFilterFieldValueNode,
} from '../utils/tree-node-guards.js';

/** @internal */
export type _FilterFieldSuggestionsContext = {
  token: FilterFieldValueNode;
  currentStatement: FilterFieldStatementNode | undefined;
  shouldEscapeSuggestion: boolean;
};

/** @internal */
export type _FilterFieldSuggestionsCallback = (args: {
  suggestionsContext: _FilterFieldSuggestionsContext;
  autoSuggestionValues: FilterFieldSuggestion[];
}) => void;

type SuggestionsExtensionProps = {
  overlayState: OverlayTriggerState;
  numberParser: NumberFormatParser;
  validatorMap?: FilterFieldValidatorMap;
  /**
   * Internal callback that is triggered whenever suggestions may need to be updated.
   * Used to update flags to determine which suggestions to show in the suggestions
   * overlay and update the suggestions context.
   */
  _onSuggest: _FilterFieldSuggestionsCallback;
  /** Consumer callback triggered whenever suggestions may need to be updated. */
  onSuggest?: FilterFieldSuggestionsCallback;
  /** Function to get the filter field syntax tree from the lezer syntax tree. */
  getSyntaxTree: ReturnType<typeof useCachedTreeConversion>['getSyntaxTree'];
};

function getCurrentToken(
  currentStatement: FilterFieldStatementNode,
  cursorPosition: number,
) {
  for (const [key, value] of Object.entries(currentStatement)) {
    if (
      isFilterFieldValueNode(value) &&
      value.range.from <= cursorPosition &&
      value.range.to >= cursorPosition
    ) {
      return {
        currentToken: value as FilterFieldValueNode,
        currentTokenType: key,
      };
    }
  }
}

/**
 * @internal
 */
export const suggestionsExtension = ({
  overlayState,
  numberParser,
  validatorMap,
  onSuggest,
  _onSuggest,
  getSyntaxTree,
}: SuggestionsExtensionProps): Extension => {
  const filterFieldSuggestion: CompletionSource = (
    context: CompletionContext,
  ) => {
    if (context.view?.composing) {
      return null;
    }

    const tree = syntaxTree(context.state);
    const { tree: convertedTree } = getSyntaxTree({
      tree,
      numberParser,
      doc: context.state.doc,
    });

    // context.pos returns the first position it finds if a selection is present, which may be the anchor.
    // We always want the cursor position, represented by the selection head.
    const { head: cursorPosition } = context.state.selection.main;
    const currentStatement = getFilterStatementForSuggestions(
      tree,
      cursorPosition,
      numberParser,
      context.state.doc,
    );

    const fallbackToken: FilterFieldLeafNode = {
      type: 'String',
      textValue: '',
      value: '',
      range: {
        from: cursorPosition,
        to: cursorPosition,
      },
    };

    let currentToken: FilterFieldValueNode | undefined = undefined;
    let currentTokenType: string | undefined;
    let providedSuggestions: FilterFieldSuggestion[] = [];
    let showInclusionOperators = false;
    let showLogicalOperators = false;
    let suggestionType: Exclude<
      Parameters<FilterFieldSuggestionsCallback>[0],
      undefined
    >['suggestionType'] = 'key';
    let shouldEscapeSuggestion = true;

    const treeCursor = tree.cursorAt(cursorPosition, -1);

    if (!currentStatement) {
      if (treeCursor.node.name === 'LogicalOperator') {
        currentToken =
          (convertedTree?.children.find(
            (token) =>
              isFilterFieldValueNode(token) &&
              token.range.from <= cursorPosition &&
              token.range.to >= cursorPosition,
          ) as FilterFieldValueNode) ?? fallbackToken;
      } else {
        currentToken = fallbackToken;
      }
    } else {
      ({ currentToken, currentTokenType } = getCurrentToken(
        currentStatement,
        cursorPosition,
      ) ?? {
        currentToken: fallbackToken,
        currentTokenType: undefined,
      });
    }

    // Move to first child ending before cursor position.
    treeCursor.childBefore(cursorPosition);
    const prevNode =
      treeCursor.node.type.is('FilterValue') ||
      treeCursor.node.parent?.type.is('FilterValue')
        ? 'FilterValue'
        : (treeCursor.node.name as FilterFieldLezerNodeType);

    // Depending on the current cursor position and the already defined parts
    // of the filter statement at the cursor position, the provided suggestions
    // and flags for which suggestions to show in the suggestions overlay are
    // set accordingly.
    if (
      ((currentStatement === undefined &&
        prevNode !== 'LogicalOperator' &&
        prevNode !== 'FilterKey' &&
        prevNode !== 'ComparisonOperator') ||
        (currentStatement && currentTokenType === 'key') ||
        (prevNode === 'LogicalOperator' && currentToken.textValue)) &&
      currentToken?.range.from !== 0
    ) {
      // Not at the first token and either:
      // - no statement at the current position e.g. "a = b |"
      // - started typing a key e.g. "a = b o|"
      // - just typed a logical operator, but no space yet e.g. "a = b or|"
      // -> show logical operator suggestions. (if the key that is typed does not start with o, the
      // or suggestion will be removed when filtering the suggestions)
      showLogicalOperators = true;
    } else if (
      (currentToken.type === 'Error' &&
        currentTokenType === 'value' &&
        !Array.isArray(currentToken.value)) ||
      (currentToken.type === 'String' &&
        !currentToken.value &&
        currentStatement &&
        !currentStatement.value &&
        prevNode !== 'FilterKey')
    ) {
      // This case handles value suggestions if there is either an invalid value in the
      // value spot (e.g. a = $) or there is no value yet
      suggestionType = 'value';
    } else if (
      currentToken.type === 'Key' &&
      ((currentStatement?.operator !== undefined &&
        currentStatement.operator.type !== 'Error') ||
        currentStatement?.value !== undefined)
    ) {
      suggestionType = 'key';
    } else if (
      (prevNode === 'FilterKey' &&
        (currentToken.type === 'String' || currentToken.type === 'Error')) ||
      currentToken.type === 'ComparisonOperator' ||
      currentToken.type === 'InclusionOperator' ||
      (prevNode === 'InvalidComparisonOperator' &&
        cursorPosition <= (currentStatement?.operator?.range.to ?? 0)) ||
      (currentToken.type === 'Error' && currentTokenType === 'operator')
    ) {
      // Previous node is a filter key and the delimiter is already entered or
      // previous node is a comparison operator -> show comparison operators.
      shouldEscapeSuggestion = false;
      suggestionType = 'comparisonOperator';
    } else if (
      ((prevNode === 'ComparisonOperator' ||
        prevNode === 'InvalidComparisonOperator') &&
        (currentStatement?.value === undefined ||
          cursorPosition === currentStatement.value.range.from)) ||
      (currentStatement?.key !== undefined &&
        treeCursor.node.parent?.name === 'FilterSimpleValue' &&
        cursorPosition === treeCursor.node.to)
    ) {
      suggestionType = 'value';
      // List suggestions need to be handled separately since we don't want to replace the whole list
      // token, but only the list values when applying suggestions. Only the list value at the cursor
      // position should be returned as `currentToken` that can be used for filtering suggestions.
    } else if (
      Array.isArray(currentToken.value) &&
      (isFilterFieldListNode(currentToken) || currentToken.type === 'Error')
    ) {
      currentToken =
        (currentToken.value?.find(
          (token) =>
            isFilterFieldLeafNode(token) &&
            token.range.from <= cursorPosition &&
            token.range.to >= cursorPosition,
        ) as FilterFieldValueNode) ?? currentToken;

      // If the current token is still the list or error, no value nodes exists yet.
      // Depending on the cursor position, we return either the fallback token or the
      // currentToken.
      /* Examples:
        - a in |()    -> return list token, whole list will be replaced.
        - a in |(     -> return error token, whole error will be replaced.
        - a in (|)    -> return fallback token, suggestion will be added to the list.
        - a in (|     -> return fallback token, suggestion will be added to the list.
        - a in (a, |  -> return fallback token, suggestion will be added to the list.
        - a in (a, |) -> return fallback token, suggestion will be added to the list.
        - a in (a, )| -> return error token, whole error will be replaced.
      */
      if (currentToken.type === 'List' || currentToken.type === 'Error') {
        let nodeCursor = tree.cursorAt(cursorPosition, 1);
        if (nodeCursor.node.name === 'Filter') {
          nodeCursor = tree.cursorAt(cursorPosition, -1);
        }

        const { node } = nodeCursor.node;

        // If the node at the cursor position is one of the following, we don't
        // want to return the whole token, but only the fallback token.
        // `ClosingBracket` is excluded, since the token then also depends on the cursor position.
        const tokensInsideList = [
          'List',
          'Identifier',
          'String',
          'Boolean',
          'Number',
          'Duration',
        ];

        if (
          tokensInsideList.includes(node.name) ||
          (node.name === 'OpeningBracket' && cursorPosition > node.from) ||
          (node.name === 'ClosingBracket' && cursorPosition < node.to)
        ) {
          currentToken = fallbackToken;
        }
      }

      // Either way, the suggestion type should still be a value.
      suggestionType = 'value';
    }

    if (
      (prevNode === 'ComparisonOperator' &&
        cursorPosition > treeCursor.node.to &&
        (currentStatement?.operator?.range.to ?? 0) <
          (currentStatement?.value?.range.from ?? 0) &&
        currentTokenType !== 'operator') ||
      (prevNode === 'ComparisonOperator' && !currentStatement) ||
      prevNode === 'FilterValue' ||
      (prevNode === '⚠' && currentTokenType === 'value')
    ) {
      suggestionType = 'value';

      // Do not show value suggestions when a position operator is already present.
      // The tree navigation is a bit weird here, but there doesn't seem to be a way around it.
      const treeCursorClone = treeCursor.node.cursor();
      if (
        treeCursor.node.name === 'PositionOperator' ||
        (treeCursor.prevSibling() &&
          treeCursor.node.name === 'PositionOperator') ||
        (treeCursorClone.nextSibling() &&
          treeCursorClone.firstChild() &&
          treeCursorClone.node.name === 'PositionOperator')
      ) {
        suggestionType = 'none';
      }
    }

    if (
      prevNode === 'InclusionOperator' &&
      cursorPosition > treeCursor.node.to &&
      treeCursor.nextSibling() &&
      (treeCursor.node.name === 'FilterIncludesValue' ||
        treeCursor.node.name === '⚠')
    ) {
      const { from } = treeCursor.node;
      const characterBeforeCursor = getTextFromRange(
        {
          from: from - 1,
          to: from,
        },
        context.state.doc,
      );

      if (characterBeforeCursor === ' ') {
        showInclusionOperators = true;
        shouldEscapeSuggestion = false;
      }

      suggestionType = 'none';
    }

    if (
      prevNode === 'OpeningBracket' &&
      treeCursor.nextSibling() &&
      treeCursor.node.name === '⚠'
    ) {
      showInclusionOperators = true;
      shouldEscapeSuggestion = false;
    }

    if (prevNode === 'ExistsOperator' || prevNode === 'PositionOperator') {
      suggestionType = 'none';
    }

    switch (suggestionType) {
      case 'comparisonOperator':
        providedSuggestions = getComparisonOperatorSuggestions(
          currentStatement?.key?.value,
          validatorMap,
        );
        break;
      case 'key':
        providedSuggestions = getKeySuggestions(
          validatorMap,
          currentToken.textValue,
        );
        break;
      case 'value': {
        providedSuggestions = getValueSuggestions(
          currentStatement?.key?.value,
          validatorMap,
          currentToken,
        );
        break;
      }
      default:
        break;
    }

    if (
      suggestionType === 'value' &&
      currentStatement?.operator?.textValue === 'not' &&
      (prevNode === 'InvalidComparisonOperator' ||
        (prevNode === 'FilterValue' &&
          currentStatement.value?.textValue === 'i'))
    ) {
      providedSuggestions = [
        ...getComparisonOperatorSuggestions(undefined, undefined).filter(
          (suggestion) => suggestion.value?.toLowerCase().includes('not in'),
        ),
      ];
    }

    if (showInclusionOperators) {
      providedSuggestions = [
        ...getComparisonOperatorSuggestions(undefined, undefined).filter(
          (suggestion) => suggestion.value?.toLowerCase().includes('in'),
        ),
        ...providedSuggestions,
      ];
    }

    if (showLogicalOperators) {
      providedSuggestions = [
        ...providedSuggestions,
        ...defaultLogicalOperatorSuggestions,
      ];
    }

    const currentValue =
      currentToken.type === 'String' || currentToken.type === 'Key'
        ? currentToken.value
        : currentToken.textValue;

    let filteredSuggestions = providedSuggestions.filter((suggestion) => {
      if (Array.isArray(currentToken.value)) {
        return true;
      }

      if (!suggestion.value) {
        return true;
      }

      // This check is needed to correctly filter the OR suggestion if there
      // are quotes or escaped characters
      if (
        suggestion.value === 'OR' &&
        currentToken.value !== currentToken.textValue
      ) {
        return false;
      }

      return suggestion.value
        ?.toLowerCase()
        .includes(`${currentValue.toLowerCase() ?? ''}`);
    });

    if (
      (suggestionType === 'key' || suggestionType === 'value') &&
      filteredSuggestions.length === 0 &&
      providedSuggestions.length !== 0 &&
      // The following part checks that we only show the 'No matching suggestions'
      // message if there were suggestions other than the 'OR' suggestions
      // that have been filtered out (in case there was only the 'OR' there should
      // be a different message)
      (!showLogicalOperators ||
        (showLogicalOperators &&
          providedSuggestions.length -
            defaultLogicalOperatorSuggestions.length !==
            0))
    ) {
      filteredSuggestions = [getNoMatchingSuggestion()];
    }

    _onSuggest({
      suggestionsContext: {
        token: currentToken,
        currentStatement,
        shouldEscapeSuggestion,
      },
      autoSuggestionValues: filteredSuggestions,
    });

    onSuggest?.({
      suggestionType,
      currentToken,
      value: context.state.doc.toString(),
      autoSuggestions: filteredSuggestions,
      syntaxTree: convertedTree,
      cursorPosition,
      currentStatement,
    });

    // Open the suggestions overlay only after the callbacks have been called.
    // Else, previous suggestions may still be rendered in the overlay and flicker
    // when updated in the most recent call to 'onSuggest'.
    overlayState.open();

    // Return empty suggestions array to avoid opening codemirror's suggestions overlay.
    return null;
  };

  return [
    autocompletion({ override: [filterFieldSuggestion] }),
    EditorView.updateListener.of((update) => {
      // Don't do anything while a character composition is ongoing. Users would no longer
      // be able to select in the autocompletion dropdown for e.g. completing weird japanese shit.
      if (update.view.composing) {
        return;
      }

      // Trigger autocompletion to call the `onAutocomplete` callback and open the
      // suggestions overlay.
      if (
        (update.focusChanged && update.view.hasFocus) ||
        update.transactions.some(
          (transaction) =>
            transaction.isUserEvent('input') ||
            transaction.isUserEvent(ANNOTATION_SUGGESTION_APPLY) ||
            transaction.isUserEvent('delete') ||
            transaction.selection !== undefined,
        )
      ) {
        startCompletion(update.view);
        return;
      }

      if (!update.view.hasFocus) {
        overlayState.close();
        return;
      }
    }),
  ];
};
